import * as Schema from "effect/Schema";

import { TrimmedNonEmptyString } from "./baseSchemas";
import { TerminalSessionStatus } from "./terminal";

/**
 * `subscribeTerminalMetadata`, the terminal list feed the vendored t3 client
 * runtime expects.
 *
 * Forge streams per-session events (`subscribeTerminalEvents`) and serves one
 * session's buffer (`terminal.attach`), but has nothing that answers "which
 * terminals exist right now, and what are they doing". The mobile app needs
 * exactly that to render its terminal list, and without the method the client's
 * request dies as `Unknown request tag: subscribeTerminalMetadata` — which takes
 * down the unrelated data commands sharing that runtime, so the symptom appears
 * on screens that have nothing to do with terminals.
 *
 * These shapes mirror `packages/t3-contracts/src/terminal.ts`, because the mobile
 * client decodes with those. `TerminalSessionStatus` is already identical on both
 * sides, so it is reused rather than restated.
 */

const TerminalSummaryLabel = Schema.String.check(Schema.isMaxLength(128));

export const TerminalSummary = Schema.Struct({
  threadId: Schema.String.check(Schema.isNonEmpty()),
  terminalId: Schema.String.check(Schema.isNonEmpty()),
  cwd: Schema.String.check(Schema.isNonEmpty()),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  status: TerminalSessionStatus,
  pid: Schema.NullOr(Schema.Int.check(Schema.isGreaterThan(0))),
  exitCode: Schema.NullOr(Schema.Int),
  exitSignal: Schema.NullOr(Schema.Int),
  hasRunningSubprocess: Schema.Boolean,
  /**
   * Optional on both sides. Forge does not compute a display title, so it is
   * omitted rather than guessed; the client falls back to its own label.
   */
  label: Schema.optional(TerminalSummaryLabel),
  updatedAt: Schema.String,
});
export type TerminalSummary = typeof TerminalSummary.Type;

export const TerminalMetadataStreamEvent = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("snapshot"),
    terminals: Schema.Array(TerminalSummary),
  }),
  Schema.Struct({
    type: Schema.Literal("upsert"),
    terminal: TerminalSummary,
  }),
  Schema.Struct({
    type: Schema.Literal("remove"),
    threadId: Schema.String.check(Schema.isNonEmpty()),
    terminalId: Schema.String.check(Schema.isNonEmpty()),
  }),
]);
export type TerminalMetadataStreamEvent = typeof TerminalMetadataStreamEvent.Type;
